import { getJwt, isJwtExpired, clearAuth, getUser } from "./auth";
import { getYearGanZhiByPreciseLiChun } from "./lunarGanZhi"
import { toPinyinSync, capitalizePinyin } from './pinyinUtil';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const GLOBAL_CACHE_KEY = "global_variables_cache";
const GLOBAL_CACHE_TIME = 24 * 60 * 60 * 1000; // 24 jam

// wkCommon.permissions.js (paste ke wkCommon.js atau gabungkan)
// Simple sync permission API: getUmatPermissions returns cached list (sync) and
// triggers background fetch if missing. hasUmatPermission reads cache sync.

// di render / komponen
//const canEditAgenda = hasUmatPermission(user.LoginEmail, PERMISSIONS.AGENDA_WRITE);

const PERMISSIONS = Object.freeze({
  AGENDA_EXPENSE_READ: "AgendaExpense_Read", 
  AGENDA_EXPENSE_WRITE: "AgendaExpense_Write",
  AGENDA_PERSONNEL_READ: "AgendaPersonnel_Read", 
  AGENDA_PERSONNEL_WRITE: "AgendaPersonnel_Write",
  AGENDA_PREREG_READ: "AgendaPreRegistration_Read",
  AGENDA_PREREG_WRITE: "AgendaPreRegistration_Write",
  AGENDA_RUNDOWN_READ: "AgendaRundown_Read", 
  AGENDA_RUNDOWN_WRITE: "AgendaRundown_Write",
  AGENDA_WRITE: "Agenda_Write",
  AGENDA_PREREGFORM_EDITOR: "Agenda_PreRegForm_Editor",
  CONFIGROLEPERMISSION_EDIT: "ConfigRolePermission_Edit",
  EVENT_EDIT: "Event_Edit",
  EVENTPARTICIPANT_READ: "EventParticipant_Read",
  EVENTPARTICIPANT_WRITE: "EventParticipant_Write",
  MEDIAFILE_BASIC: "MediaFile_Basic",
  MEDIAFILE_FULL: "MediaFile_Full",
  MEDIAFILE_EDITOR: "MediaFile_Editor",
  QIUDAOREN_FIND: "QiuDaoRen_Find",
  QIUDAOREN_READ: "QiuDaoRen_Read",
  QIUDAOREN_WRITE: "QiuDaoRen_Write",
  REPORT_FINANCE_VIEW: "ReportFinance_View",
  REPORT_GENERAL_VIEW: "ReportGeneral_View",
  REPORT_PARTICIPANT_VIEW: "ReportParticipant_View",
  UMATDATA_ADD: "UmatData_Add",
  UMATDATA_BASIC: "UmatData_Basic",
  UMATDATA_EDIT: "UmatData_Edit",
  UMATDATA_EDITAPPROVAL: "UmatData_EditApproval", 
  UMATDATA_FULL: "UmatData_Full",
  UMATDATA_LISTEXPORT: "UmatData_ListExport",
  UMATPERMISSION_READ: "UmatPermission_Read", 
  UMATPERMISSION_WRITE: "UmatPermission_Write",
  AGENDABOOK_SAVETEMPLATE: "AgendaBook_SaveTemplate",
});
export default PERMISSIONS;


const _permsCache = new Map(); // key: emailString => { status: "pending"|"ok"|"err", value, promise, error }
const _inflight = new Map(); // key => Promise  (NOT stored in _permsCache)

/** optional: timeout wrapper to avoid forever-pending fetch */
function withTimeout(promise, ms = 15000) {
  let id;
  const timeout = new Promise((_, reject) => {
    id = setTimeout(() => reject(new Error("timeout")), ms);
  });
  return Promise.race([
    promise.finally(() => clearTimeout(id)),
    timeout
  ]);
}

/**
 * invalidateUmatPermissionsCache(email?)
 * - email undefined/null => clear all cache
 */
export function invalidateUmatPermissionsCache(email = null) {
  if (!email) _permsCache.clear();
  else _permsCache.delete(String(email));
}

/**
 * Internal async fetcher that DOES NOT store promise in the cache.
 * - Deduping for concurrent callers is implemented via _inflight map (not cache).
 * - On success: stores { status: 'ok', value: details } in _permsCache and removes inflight entry.
 * - On failure: stores { status: 'err', error } in _permsCache and removes inflight entry.
 *
 * Usage:
 *   await _fetchAndStoreUmatPermissions(email);
 *
 * Returns the details object on success, or null on error.
 */
async function _fetchAndStoreUmatPermissions(email) {
  const key = String(email ?? "");

  //console.debug("[perms] start fetch", {
  //  email: key,
  //  time: new Date().toISOString()
  //});
  
  // If already cached as OK, return it immediately
  const cached = _permsCache.get(key);
  if (cached && cached.status === "ok") return cached.value;

  // If there's an inflight promise, return it (dedupe) — but DO NOT put promise into _permsCache.
  if (_inflight.has(key)) {
    try {
      return await _inflight.get(key);
    } catch (err) {
      // inflight failed; fallthrough to attempt new fetch
    }
  }

  // Start a new fetch and store promise in _inflight only
  const fetchPromise = (async () => {
    try {
      // build request path (adjust populate if needed)
      const encodedEmail = encodeURIComponent(email);
      const path = `/api/umats?filters%5BLoginEmail%5D%5B$eq%5D=${encodedEmail}&populate%5Bumat_roles%5D%5Bpopulate%5D=umat_permissions`;
      
      //console.debug("[perms] requesting API", path);

      // call apiFetch with timeout to avoid hanging forever
      const data = await withTimeout(apiFetch(path), 15000);

      const item = Array.isArray(data?.data) ? data.data[0] : data?.data;
      if (!item) {
        const empty = { direct: [], fromRoles: [], all: [] };
        // store result into cache (ok but empty)
        _permsCache.set(key, { status: "ok", value: empty });
        return empty;
      }

      const attributes = item.attributes ?? item;

      // direct permissions
      let directPerms = [];
      const up = attributes.umat_permissions ?? attributes.permissions ?? null;
      if (up) {
        const list = Array.isArray(up) ? up : up?.data ?? [];
        directPerms = list.map((p) => p?.attributes?.name ?? p?.name).filter(Boolean);
      }

      // permissions from roles
      let fromRoles = [];
      const roles = attributes.umat_roles ?? attributes.roles ?? null;
      const roleList = Array.isArray(roles) ? roles : roles?.data ?? [];
      for (const r of roleList) {
        const rattr = r?.attributes ?? r;
        const perms = rattr?.umat_permissions ?? rattr?.permissions ?? null;
        if (!perms) continue;
        const pList = Array.isArray(perms) ? perms : perms?.data ?? [];
        pList.forEach((pp) => {
          const name = pp?.attributes?.name ?? pp?.name;
          if (name) fromRoles.push(name);
        });
      }

      const all = Array.from(new Set([...directPerms, ...fromRoles]));
      
      //console.debug("[perms] permissions loaded", {
      //  email: key,
      //  direct: directPerms.length,
      //  fromRoles: fromRoles.length,
      //  total: all.length,
      //  perms: all
      //});

      const details = { direct: directPerms, fromRoles, all };

      // store ONLY AFTER we have the result
      _permsCache.set(key, { status: "ok", value: details });

      return details;
    } catch (err) {
      // save error state so caller knows cached state is err
      console.warn("[perms] fetch error for", key, err);
      //_permsCache.set(key, { status: "err", error: err });
      // jangan cache error
      _permsCache.delete(key);
      return null;
    } finally {
      // remove inflight so subsequent attempts can retry
      _inflight.delete(key);
    }
  })();

  // store promise only in inflight map (not in _permsCache)
  _inflight.set(key, fetchPromise);

  // await and return the result
  try {
    const result = await fetchPromise;
    return result;
  } catch (err) {
    // fetchPromise should have handled errors and returned null, but catch defensively
    return null;
  }
}


/**
 * getUmatPermissions(email)
 *
 * Sync API:
 *  - if cached -> return array of permission names (details.all)
 *  - if not cached -> starts background fetch and returns []
 *
 * NOTE: to get guaranteed up-to-date data before rendering, call the internal
 * fetch helper from login/parent logic (not exposed here) or check cache readiness.
 */
export function getUmatPermissions(email) {
  const key = String(email ?? "");
  const entry = _permsCache.get(key);
  if (entry && entry.status === "ok" && entry.value && Array.isArray(entry.value.all)) {
    return entry.value.all.slice(); // return copy of array
  }

  // not cached yet -> trigger background fetch (fire-and-forget)
  // do not await here (public API remains sync)
  _fetchAndStoreUmatPermissions(email).catch(() => {});
  return [];
}

/**
 * hasUmatPermission(email, permissionName)
 *
 * Sync boolean:
 *  - if cached -> return true/false
 *  - if not cached -> trigger background fetch and return false
 */
export function hasUmatPermission(email, permissionName) {
  if (!permissionName) return false;
  const key = String(email ?? "");
  const entry = _permsCache.get(key);

  //console.debug("[perms] check", {
  //  email:key,
  //  permission:permissionName,
  //  cache:entry?.status
  //});

  //console.log ("cache entry: ", entry)
  if (entry && entry.status === "ok" && entry.value && Array.isArray(entry.value.all)) {
    //console.log ("permission name: ", permissionName, "; owned: ",entry.value.all.includes(permissionName))
    return entry.value.all.includes(permissionName);
  }

  // not ready -> trigger background fetch and return false for now
  _fetchAndStoreUmatPermissions(email).catch(() => {});
  return false;
}

//cek akses folder, dipakai di DocumentsPage.js
export function canAccessFolder(folder, user) {
  if (!folder) return false;

  const type = folder.access_type || "authenticated";

  // PUBLIC
  if (type === "public") return true;

  // AUTHENTICATED
  if (type === "authenticated") {
    return !!user;
  }

  // ROLE BASED
  if (type === "role_based") {
    if (!user) return false;

    const allowed = folder.allowed_roles || [];

    const userRoles =
      user?.umat?.umat_roles ||
      user?.umat_roles ||
      [];

    const allowedSlugs = allowed.map(r => r.slug || r.attributes?.slug);

    return userRoles.some(r =>
      allowedSlugs.includes(r.slug || r.attributes?.slug)
    );
  }

  return false;
}

const ACCESS_LEVEL = {
  public: 1,
  authenticated: 2,
  role_based: 3
};

export function validateFolderAccess(folder) {
  const parent = folder?.document_folder;
  if (!parent) return null;

  const childLevel = ACCESS_LEVEL[folder.access_type || "authenticated"];
  const parentLevel = ACCESS_LEVEL[parent.access_type || "authenticated"];

  // ❗ child lebih longgar dari parent
  if (childLevel < parentLevel) {
    return {
      warning: true,
      message: `Folder "${folder.name}" lebih terbuka dari parent "${parent.name}"`
    };
  }

  return null;
}

/**
 * Extract profilepic URL from a Strapi v5 populated media field.
 * Expects the canonical v5 shape: item.attributes.profilepic.data
 *
 * - Prefers thumbnail/small/medium formats if available.
 * - Returns fully qualified URL (prefixes API_URL for relative URLs).
 * - Returns null when profilepic is not present or not populated.
 *
 * NOTE: This function strictly reads the Strapi v5 populated media shape.
 */
export function extractProfilepicUrlFromAttributes(item) {
  if (!item) return null;

  // Helper to turn a relative url into absolute using API_URL
  const absolutize = (u) => (u && u.startsWith("http") ? u : (u ? `${API_URL}${u}` : null));

  // 1) canonical Strapi v5 populated single media field
  const pPop = item?.profilepic?.data || item?.attributes?.profilepic?.data;
  if (pPop && pPop.attributes) {
    const formats = pPop.attributes.formats || {};
    const thumb =
      formats.thumbnail?.url ||
      formats.small?.url ||
      formats.medium?.url;
    const rel = thumb || pPop.attributes.url || null;
    return absolutize(rel);
  }

  // 2) flat media object on top-level (your current API response)
  const pFlat = item?.profilepic;
  if (pFlat && (pFlat.url || pFlat.formats)) {
    const formats = pFlat.formats || {};
    const thumb =
      formats.thumbnail?.url ||
      formats.small?.url ||
      formats.medium?.url;
    const rel = thumb || pFlat.url || null;
    return absolutize(rel);
  }

  // 3) sometimes attributes.profilepic itself is the object (unusual but possible)
  const pAttrsObj = item?.attributes?.profilepic;
  if (pAttrsObj && (pAttrsObj.url || pAttrsObj.formats)) {
    const formats = pAttrsObj.formats || {};
    const thumb =
      formats.thumbnail?.url ||
      formats.small?.url ||
      formats.medium?.url;
    const rel = thumb || pAttrsObj.url || null;
    return absolutize(rel);
  }

  return null;
}

/**
 * Convenience: resolve avatar url for a rendered "r" record.
 * Tries:
 *  1) extractProfilepicUrlFromAttributes(r)
 *  2) r.avatar (if present and already a URL)
 *  3) getAvatarPlaceholder(r.gender)  <-- you likely already have this in wkCommon
 */
export function resolveUmatAvatarSync(r) {
  // ensure extractProfilepicUrlFromAttributes uses API_URL from this file
  const fromProfilepic = extractProfilepicUrlFromAttributes(r);
  if (fromProfilepic) return fromProfilepic;

  if (r?.avatar) return r.avatar.startsWith("http") ? r.avatar : `${API_URL}${r.avatar}`;

  // fallback to placeholder (assumes getAvatarPlaceholder is already exported in wkCommon)
  if (typeof getAvatarPlaceholder === "function") return getAvatarPlaceholder(r?.gender);
  return null;
}

/**
 * Avatar util - shared function
 * - accepts gender (string), optional avatarUrl override
 * - returns a placeholder path when avatarUrl is falsy
 * - paths assume you place placeholder images under public/avatars/
 */
export function getAvatarPlaceholder(gender = "") {
  const g = (gender || "").toString().toLowerCase();
  const isFemale = /(^f$)|female|wanita|perempuan|f$/i.test(g);
  const isMale = /(^m$)|male|pria|laki|laki-laki|cowok/i.test(g);

  const MALE_FILES = ["/avatars/male1.png", "/avatars/male2.png", "/avatars/male3.png", "/avatars/male4.png"];
  const FEMALE_FILES = ["/avatars/female1.png", "/avatars/female2.png", "/avatars/female3.png", "/avatars/female4.png"];

  const pool = isFemale ? FEMALE_FILES : (isMale ? MALE_FILES : (Math.random() < 0.5 ? MALE_FILES : FEMALE_FILES));
  const idx = Math.floor(Math.random() * pool.length);
  return pool[idx];
}

export async function apiFetchFormData(pathOrUrl, formData, opts = {}) {
  if (!(formData instanceof FormData)) {
    throw new Error("apiFetchFormData expects a FormData instance as second argument.");
  }

  const url = pathOrUrl && (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://"))
    ? pathOrUrl
    : `${API_URL}${pathOrUrl}`;

  // hanya pasang Authorization header (jangan set Content-Type)
  const headers = opts.headers ? { ...opts.headers } : {};
  const token = getJwt();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(url, {
    method: opts.method || "POST",
    headers,   // NO 'Content-Type' for FormData
    body: formData,
  });

  // response handling mirip apiFetch: parse JSON or text, dan throw error kalau !ok
  const text = await response.text().catch(() => "");
  let json = null;
  try { json = text ? JSON.parse(text) : null; } catch (e) { json = text; }

  if (!response.ok) {
    const msg = typeof json === "string" ? json : JSON.stringify(json);
    const err = new Error(`apiFetchFormData HTTP ${response.status} - ${msg}`);
    err.status = response.status;
    err.body = json;
    throw err;
  }

  return json;
}

// small wrapper: uses Authorization header if JWT present in localStorage
export async function apiFetch(pathOrUrl, opts = {}) {
  // allow passing full URL or relative path that we'll prefix with API_URL
  const url = pathOrUrl && (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://"))
    ? pathOrUrl
    : `${API_URL}${pathOrUrl}`;

  const headers = opts.headers ? { ...opts.headers } : {};

  // gunakan getJwt() dari auth.js (auth.js menyimpan jwt di dalam STORAGE_KEY)
  const token = getJwt();
  //if (token) headers['Authorization'] = `Bearer ${token}`;
  if (token) {
    const payload = JSON.parse(atob(token.split('.')[1]));
    //console.debug("[jwt] exp:", new Date(payload.exp * 1000));
    
    if (isJwtExpired(token)) {
      console.warn("apiFetch: expired JWT detected → clearing auth");
      clearAuth();
    } else {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  let body = opts.body;
  if (body && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json; charset=utf-8';
    body = JSON.stringify(body);
  }

  let response;

  try {
    response = await fetch(url, {
      method: opts.method || (body ? "POST" : "GET"),
      headers,
      body,
    });
  } catch (err) {
    if (!navigator.onLine) {
      console.warn("NO_INTERNET", err);
      window.dispatchEvent(new Event("backendDown"));
      return null;
    }
    console.warn("BACKEND_UNREACHABLE", err);
    window.dispatchEvent(new Event("backendDown"));
    return null;
  }

  if (!response.ok) {
    let errorBody = null;
    try {
      errorBody =
        await response.json();
    } catch (e) {
      console.warn("FAILED_PARSE_ERROR_BODY", e);
    }

    console.error(
      "API_ERROR_RESPONSE",
      {
        status: response.status,
        statusText: response.statusText,
        body: errorBody,
      }
    );

    if (response.status >= 500) {
      console.warn("BACKEND_ERROR");
      throw new Error(
        errorBody?.error?.message ||
        "Backend error"
      );
    }

    if (
      response.status === 401 ||
      response.status === 403
    ) {
      if (response.status === 401) {
        clearAuth();
        console.warn("AUTH_INVALID");
      } else {
        console.warn("FORBIDDEN_403");
     }

      throw new Error(
        errorBody?.error?.message ||
        `HTTP_${response.status}`
      );
    }

    const validationErrors =
      errorBody?.error?.details?.errors;

    if (
      Array.isArray(validationErrors)
    ) {

      console.error(
        "STRAPI_VALIDATION_ERRORS",
        validationErrors
      );
    }

    throw new Error(
      errorBody?.error?.message ||
      `HTTP_${response.status}`
    );
  }

  // ✅ HANDLE 204 NO CONTENT (SAFE FIX)
  if (response.status === 204) {
    return null;
  }
  
  // parse JSON if possible
  let json = null;
  try {
    json = await response.json();
  } catch (e) {
    // not JSON or empty body
    const text = await response.text().catch(() => "");
    throw new Error(`apiFetch: response not JSON (${response.status}) - ${text}`);
  }

  if (!response.ok) {
    // throw with details so caller can present error
    throw new Error(`apiFetch HTTP ${response.status} - ${JSON.stringify(json)}`);
  }

  return json;
}

export async function getUmatByEmail(email) {
  if (!email) throw new Error('email is required');

  const q = encodeURIComponent(email);
  const url = `${API_URL}/api/umats?filters%5BLoginEmail%5D%5B$eq%5D=${q}&populate=*`;

  // Attach Authorization header like LoginRedirect does (some Strapi setups require auth)
  const headers = {};
  const token = getJwt();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const r = await fetch(url, { headers });

  // Helpful debug: show body when not OK (will reveal Strapi error details)
  const bodyText = await r.clone().text().catch(() => '');
  if (!r.ok) {
    // include useful debug information in the thrown error for dev
    console.warn('getUmatByEmail fetch failed', { url, status: r.status, body: bodyText });
    const err = new Error(`getUmatByEmail HTTP ${r.status} - ${bodyText}`);
    err.status = r.status;
    err.body = bodyText;
    throw err;
  }

  const j = await r.json().catch(() => null);
  const arr = j?.data || j?.umats || null;
  if (!arr || !Array.isArray(arr) || arr.length === 0) return null;
  return arr[0];
}

export function isLocalhost() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host.startsWith("192.168.") ||
    host.startsWith("10.");
}

export async function fetchAndStoreUmatPermissions(email) {
  // reuse internal function already defined in wkCommon.js
  // if internal name is _fetchAndStoreUmatPermissions, call it directly
  return await _fetchAndStoreUmatPermissions(email);
}


// --- Lunar map helpers ---
// Fetch lunar-dates records between startDate..endDate and return a map keyed by YYYY-MM-DD.
// startDate / endDate may be Date objects or strings like "2025-11-26".
// Returns: { map: { "YYYY-MM-DD": { lunarMonth, lunarDay, leapMonth, lunarGanZhi, id, raw } }, count: N, sourceField: "date"|"dateInt", urls: { last } }
export async function fetchLunarMap(startDate, endDate) {
  // normalize to ISO bounds in UTC (keep only date portion as keys)
  const toStartIso = (d) => {
    if (d instanceof Date) {
      // make at 00:00:00 UTC
      return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0)).toISOString().slice(0, 19) + "Z";
    }
    // assume "YYYY-MM-DD" string
    return String(d) + "T00:00:00Z";
  };
  const toEndIso = (d) => {
    if (d instanceof Date) {
      // make at 23:59:59 UTC
      return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59)).toISOString().slice(0, 19) + "Z";
    }
    return String(d) + "T23:59:59Z";
  };

  const startIso = toStartIso(startDate);
  const endIso = toEndIso(endDate);

  async function tryField(fieldName) {
    const params = new URLSearchParams();
    params.set(`filters[${fieldName}][$gte]`, startIso);
    params.set(`filters[${fieldName}][$lte]`, endIso);
    // increase pagination size so we get many records in one fetch (adjust if your dataset bigger)
    params.set("pagination[pageSize]", "500");
    // populate if you need nested fields (not necessary for mapping), but keep minimal
    const path = `/api/lunar-dates?${params.toString()}`;
    // use apiFetch which will prefix API_URL and attach Authorization when needed
    const json = await apiFetch(path);
    // normalize to array
    return Array.isArray(json?.data) ? json.data : (json?.data ? [json.data] : []);
  }

  // try 'date' then 'dateInt' (some installs use either)
  let items = [];
  let sourceField = null;
  try {
    items = await tryField("date");
    if (items && items.length > 0) sourceField = "date";
  } catch (err) {
    // ignore and try next
    items = [];
  }
  if ((!items || items.length === 0)) {
    try {
      items = await tryField("dateInt");
      if (items && items.length > 0) sourceField = "dateInt";
    } catch (err) {
      items = [];
    }
  }

  const map = {};
  (items || []).forEach((it) => {
    const rec = it.attributes ? ({ id: it.id, ...it.attributes }) : it;
    const raw = rec.date || rec.dateInt || rec.dateString || null;
    const dateKey = raw ? String(raw).split("T")[0] : null;
    if (dateKey) {
      const ganZhiByLiChun = getYearGanZhiByPreciseLiChun(dateKey);
      map[dateKey] = {
        id: rec.id ?? rec.id,
        lunarMonth: rec.lunarMonth,
        lunarDay: rec.lunarDay,
        leapMonth: rec.leapMonth,
        lunarGanZhi: ganZhiByLiChun,
        raw,
      };
    }
  });

  return { map, count: (items || []).length, sourceField, urls: { last: `/api/lunar-dates?start=${startIso}&end=${endIso}` } };
}

// Simple getter (untuk rentang tanggal - lunarMap)
export function getLunarForDate(lunarMap, dateKey) {
  if (!lunarMap || !dateKey) return null;
  return lunarMap[dateKey] || null;
}

// Simple getter (untuk satu tanggal)
export async function fetchLunarForIntDate(intDate) {
  if (!intDate) return null;
  const dateKey = String(intDate).split("T")[0];

  const url = `/api/lunar-dates?filters[dateInt][$eq]=${dateKey}&pagination[pageSize]=1`;
  const json = await apiFetch(url);

  const item = json?.data?.[0];
  if (!item) return null;

  const a = item;
  const ganZhiByLiChun = getYearGanZhiByPreciseLiChun(dateKey);

  return {
    lunarMonth: a.lunarMonth,
    lunarDay: a.lunarDay,
    leapMonth: a.leapMonth,
    lunarGanZhi: ganZhiByLiChun,
    dateKey
  };
}

export function safeGoBack(history, fallback = "/") {
  if (!history) {
    window.location.href = fallback;
    return;
  }

  if (history.length > 1) {
    history.goBack();
  } else {
    history.replace(fallback);
  }
}

// Get Global Variables
export async function getGlobalVariables(keynames = []) {

  if (!Array.isArray(keynames)) keynames = [keynames];

  try {

    const raw = localStorage.getItem(GLOBAL_CACHE_KEY);

    if (raw) {
      const parsed = JSON.parse(raw);

      if (Date.now() - parsed.time < GLOBAL_CACHE_TIME) {

        const result = {};
        keynames.forEach(k => {
          if (parsed.data[k] !== undefined) {
            result[k] = parsed.data[k];
          }
        });

        // jika semua key ada di cache → langsung return
        if (Object.keys(result).length === keynames.length) {
          return result;
        }
      }

    }

  } catch {}

  const params = new URLSearchParams();

  keynames.forEach((k, i) => {
    params.append(`filters[keyname][$in][${i}]`, k);
  });

  const url = `/api/global-variables?${params.toString()}`;

  const resp = await apiFetch(url);

  const arr = resp?.data || [];
  const map = {};

  arr.forEach(item => {
    const key = item.keyname || item.attributes?.keyname;
    const val = item.keyvalue || item.attributes?.keyvalue;
    if (key) map[key] = val;
  });

  // merge dengan cache lama
  let cacheData = {};
  try {
    const old = JSON.parse(localStorage.getItem(GLOBAL_CACHE_KEY));
    cacheData = old?.data || {};
  } catch {}

  const merged = { ...cacheData, ...map };

  localStorage.setItem(
    GLOBAL_CACHE_KEY,
    JSON.stringify({
      time: Date.now(),
      data: merged
    })
  );

  const result = {};
  keynames.forEach(k => {
    result[k] = merged[k] || null;
  });

  return result;
}

export async function getRoleTemplates() {

  const vars = await getGlobalVariables([
    "GROUP_ROLE_TEMPLATES",
    "SERVICE_ROLE_TEMPLATES"
  ]);

  return {
    group: vars.GROUP_ROLE_TEMPLATES || [],
    service: vars.SERVICE_ROLE_TEMPLATES || []
  };
}


export async function getRundownTemplates() {

  const vars = await getGlobalVariables([
    "RUNDOWN_TEMPLATES"
  ]);

  return {
    rundown: vars.RUNDOWN_TEMPLATES || []
  };
}

const KEY = "jubilees_cache_v1";
const TTL = 1000 * 60 * 60 * 12; // 12 jam (bebas: 6–24 jam juga oke)

export async function getJubileesCached() {
  try {
    const raw = localStorage.getItem(KEY);

    if (raw) {
      const parsed = JSON.parse(raw);

      if (Date.now() - parsed.time < TTL) {
        return parsed.data;
      }
    }
  } catch (e) {
    console.warn("cache read error", e);
  }

  // fetch dari API (pakai fields biar ringan)
  const data = await apiFetch(
    `/api/jubilees?pagination[pageSize]=500&` +
    `fields[0]=jubileeMonth&` +
    `fields[1]=jubileeDay&` +
    `fields[2]=jubileeLunar&` +
    `fields[3]=jubileeDesc&` +
    `fields[4]=xianGong&` +
    `fields[5]=tataKrama`
  );

  try {
    localStorage.setItem(KEY, JSON.stringify({
      time: Date.now(),
      data
    }));
  } catch (e) {
    console.warn("cache write error", e);
  }

  return data;
}

//DISPLAY UMAT
//LABEL BARIS 1, BARIS 2 & BARIS 3

export function getUmatDisplayLabelLine2(u) {
  const chinese = (u?.chineseName || "").trim();
  const full = (u?.fullName || "").trim();
  const pinyin = (u?.pinyinName || "").trim();
  const nick = (u?.nickName || "").trim();

  if (chinese && full) return pinyin; 
  if (chinese && pinyin) return nick; 
  if (pinyin && full) return nick;
  if (chinese) return `${capitalizePinyin(toPinyinSync(chinese))}`; //generate by system utils/pinyinUtil.js
  
  return "—";
}

export function getUmatDisplayLabel(u) {
  const chinese = (u?.chineseName || "").trim();
  const full = (u?.fullName || "").trim();
  const pinyin = (u?.pinyinName || "").trim();
  const nick = (u?.nickName || "").trim();

  if (chinese && full) return `${chinese} ${full}`; //line 2: pinyin jika ada
  if (chinese && pinyin) return `${chinese} ${pinyin}`; //line 2: nickname jika ada
  if (pinyin && full) return `${pinyin} / ${full}`; //line 2: nickname jika ada
  if (chinese) return chinese; //line 2 pinyin -generate by system utils/pinyinUtil.js
  if (pinyin) return pinyin; //line 2 kosong
  if (full) return full; //line 2 kosong
  if (nick) return nick; //line 2 kosong
  
  return u?.umatID ?? "—";
}

export function getUmatMetaLine(u) {
  const kode = u?.umatID || "-";
  const gender = u?.gender || "-";
  const area = u?.raw?.area?.name || "-";

  return `${kode} | ${gender} | ${area}`;
}

export function getUmatAreaPinyin(u) {
  const area = u?.raw?.area?.shortname || "-";
  const areaPinyin = capitalizePinyin(toPinyinSync(area));

  return `${area} ${areaPinyin}`;
}

//Log Visit
let __lastVisitKey = "";

export async function logVisit({ path, query = "" }) {
  try {
    const jwt = getJwt();
    const user = getUser();

    // ❗ hanya log kalau benar-benar authenticated
    if (!jwt || !user) return;

    const username =
      user?.username ||
      user?.email ||
      user?.umat?.displayName;

    // ❗ kalau tidak ada username → skip
    if (!username) return;

    const cleanQuery = (query || "").replace(/^\?/, "");
    const key = `${username}|${path}|${cleanQuery}`;

    // ❗ prevent duplicate (re-render / double trigger)
    if (key === __lastVisitKey) return;
    __lastVisitKey = key;

    await apiFetch("/api/visit-logs", {
      method: "POST",
      body: {
        data: {
          username,
          path,
          query: cleanQuery || null,
        },
      },
    });
  } catch (e) {
    // ❗ silent fail (tidak ganggu user & tidak spam console)
  }
}